import * as React from 'react';
import { Button } from '@/components/ui/Button';
import type { CreditFeedbackCampaign } from '@/types/creditFeedback';
import closeIcon from '@/assets/images/svg/close_icon.svg';

interface CreditFeedbackPopoverProps {
  campaign: CreditFeedbackCampaign;
  isOpen: boolean;
  isSubmitting: boolean;
  onDismiss: () => void;
  onSubmit: (answers: Record<string, string>) => void;
}

function buildRewardText(campaign: CreditFeedbackCampaign): string | null {
  const parts: string[] = [];
  if (campaign.reward_credits && campaign.reward_credits > 0) {
    parts.push(`${campaign.reward_credits} bonus credits`);
  }
  if (campaign.reward_discount_percent && campaign.reward_discount_percent > 0) {
    parts.push(`${campaign.reward_discount_percent}% off your next purchase`);
  }
  return parts.length > 0 ? `Reward: ${parts.join(' + ')}` : null;
}

/** Popover shown in chat when credits cross an admin-configured threshold. */
export const CreditFeedbackPopover: React.FC<CreditFeedbackPopoverProps> = ({
  campaign,
  isOpen,
  isSubmitting,
  onDismiss,
  onSubmit,
}) => {
  const [answers, setAnswers] = React.useState<Record<string, string>>({});
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (isOpen) {
      setAnswers({});
      setError(null);
    }
  }, [isOpen, campaign.id]);

  if (!isOpen) return null;

  const rewardText = buildRewardText(campaign);
  const sortedQuestions = [...(campaign.questions || [])].sort(
    (firstQuestion, secondQuestion) =>
      (firstQuestion.sort_order ?? 0) - (secondQuestion.sort_order ?? 0)
  );

  const handleSubmit = () => {
    for (const question of sortedQuestions) {
      if (question.required !== false && !(answers[question.id] || '').trim()) {
        setError(`Please answer: ${question.label}`);
        return;
      }
    }
    setError(null);
    onSubmit(answers);
  };

  return (
    <div
      className='absolute inset-x-4 bottom-full z-30 mb-3 xl:inset-x-[48px]'
      role='dialog'
      aria-labelledby='credit-feedback-title'
    >
      <div
        className='relative mx-auto w-full max-w-[520px] rounded-[20px] border-2 border-[#D6D1E8] bg-white p-5 shadow-xl'
        style={{ boxShadow: '0 8px 32px rgba(26, 16, 54, 0.18)' }}
      >
        <button
          type='button'
          onClick={onDismiss}
          className='absolute right-4 top-4 flex size-8 items-center justify-center rounded-full hover:bg-gray-100'
          aria-label='Dismiss'
        >
          <img src={closeIcon} alt='' className='size-4' />
        </button>

        <h3 id='credit-feedback-title' className='pr-10 font-sans text-18 font-bold text-brand-800'>
          {campaign.title}
        </h3>
        {campaign.description ? (
          <p className='mt-2 font-sans text-14 text-gray-500'>{campaign.description}</p>
        ) : null}
        {rewardText ? (
          <p className='mt-2 font-sans text-12 font-semibold text-brand-600'>{rewardText}</p>
        ) : null}

        <div className='mt-4 space-y-4'>
          {sortedQuestions.map((question) => (
            <div key={question.id}>
              <label className='mb-1 block font-sans text-14 font-medium text-brand-800'>
                {question.label}
                {question.required !== false ? <span className='text-red-400'> *</span> : null}
              </label>
              <textarea
                value={answers[question.id] || ''}
                onChange={(e) =>
                  setAnswers((prev) => ({
                    ...prev,
                    [question.id]: e.target.value,
                  }))
                }
                placeholder={question.placeholder || ''}
                rows={2}
                className='w-full resize-none rounded-xl border border-gray-350 px-3 py-2 font-sans text-14 text-brand-800 outline-none focus:border-brand-600'
              />
            </div>
          ))}
        </div>

        {error ? <p className='mt-3 font-sans text-12 text-red-500'>{error}</p> : null}

        <div className='mt-5 flex justify-end gap-3'>
          <Button type='button' variant='gray' onClick={onDismiss} disabled={isSubmitting}>
            Not now
          </Button>
          <Button type='button' onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Sending…' : 'Submit'}
          </Button>
        </div>
      </div>
    </div>
  );
};
